<template>
  <div
    v-if="visible"
    class="loader"
    :class="{ burst: bursting }"
    aria-live="polite"
  >
    <svg width="0" height="0" style="position: absolute" aria-hidden="true">
      <defs>
        <filter
          :id="gooId"
          x="-35%"
          y="-35%"
          width="170%"
          height="170%"
          color-interpolation-filters="sRGB"
        >
          <feGaussianBlur
            :id="gpreId"
            in="SourceGraphic"
            stdDeviation="0.6"
            result="pre"
          />
          <feColorMatrix
            :id="gmatId"
            in="pre"
            type="matrix"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 1 0"
            result="cut"
          />
          <feGaussianBlur in="cut" stdDeviation="1.1" />
        </filter>
        <filter
          :id="wcId"
          x="-70%"
          y="-70%"
          width="240%"
          height="240%"
          color-interpolation-filters="sRGB"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.018"
            numOctaves="3"
            seed="4"
            result="n"
          />
          <feDisplacementMap
            :id="dispId"
            in="SourceGraphic"
            in2="n"
            scale="0"
            xChannelSelector="R"
            yChannelSelector="G"
            result="d"
          />
          <feGaussianBlur :id="blurId" in="d" stdDeviation="0" />
        </filter>
      </defs>
    </svg>

    <div class="perfect-stage" aria-hidden="true">
      <div
        class="perfect-orb"
        :class="{ dissolve: dissolving }"
        ref="orb"
        :style="orbStyle"
      >
        <div class="perfect-shell" ref="shell">
          <div class="perfect-pigment" :style="pigmentStyle">
            <div class="perfect-fluid perfect-teal"></div>
            <div class="perfect-fluid perfect-pink"></div>
            <div class="perfect-fluid perfect-amber"></div>
            <div class="perfect-fluid perfect-mint"></div>
          </div>
          <div class="perfect-gloss" ref="gloss"></div>
        </div>
      </div>
    </div>

    <div class="loader-copy perfect-copy">
      <div class="loader-eyebrow">Utopie · creando con IA</div>
      <h2 ref="headline" v-html="headline"></h2>
      <span ref="loaderText">{{ detail }}</span>
      <div class="dots"><i></i><i></i><i></i></div>
    </div>
  </div>
</template>

<script>
import { wait } from "@/utils/prekVisualStream";

const MESSAGES = [
  [
    "Imaginando lindos <em>ejercicios</em>",
    "Interpretando tus objetivos y escogiendo el formato visual…",
  ],
  [
    "Mezclando educación con <em>amor</em>",
    "Cuidando cada ilustración y cada detalle para tu clase…",
  ],
];

const SPHERE = {
  sphere: { B: 0.6, M: 1, O: 0, mA: 59, mB: 61, gloss: 1 },
  wash: { B: 8, M: 16, O: -7, mA: 33, mB: 84, gloss: 0 },
};

export default {
  name: "MaterialStreamLoader",
  props: {
    // Los textos por defecto son los de materiales para la clase, para que ese
    // flujo siga exactamente igual. Planificaciones pasa los suyos.
    messages: {
      type: Array,
      default: () => MESSAGES,
    },
    morphMessage: {
      type: String,
      default: "La idea se convierte en acuarela…",
    },
    openMessage: {
      type: String,
      default: "Abriendo el lienzo de ejercicios…",
    },
    // Cuanto esperar el `releaseBurst()` del padre antes de disolverse igual.
    // 0 = esperar indefinidamente, que es lo que necesita un proceso largo:
    // en planificaciones la generacion puede pasarse de los 12s de rejilla y
    // no queremos que la burbuja se apague dejando la pantalla muerta.
    holdMs: {
      type: Number,
      default: 12000,
    },
  },
  data() {
    const uid = this._uid || Math.floor(Math.random() * 1e9);
    return {
      visible: true,
      bursting: false,
      dissolving: false,
      orbFilter: "",
      headline: (this.messages[0] || [])[0] || "",
      detail: (this.messages[0] || [])[1] || "",
      donePromise: null,
      resolveDone: null,
      gatePromise: null,
      resolveGate: null,
      messagesDone: false,
      gooId: `utopieGoo-${uid}`,
      gpreId: `utopieGpre-${uid}`,
      gmatId: `utopieGmat-${uid}`,
      wcId: `utopieWc-${uid}`,
      dispId: `utopieDisp-${uid}`,
      blurId: `utopieBlur-${uid}`,
    };
  },
  created() {
    this.donePromise = new Promise((resolve) => {
      this.resolveDone = resolve;
    });
    this.gatePromise = new Promise((resolve) => {
      this.resolveGate = resolve;
    });
  },
  mounted() {
    this.$nextTick(() => {
      this.resetPerfectLoader();
      this.runSequence();
    });
  },
  computed: {
    pigmentStyle() {
      return {
        filter: `url(#${this.gooId}) brightness(1.06) saturate(1.06)`,
      };
    },
    orbStyle() {
      return this.orbFilter ? { filter: this.orbFilter } : {};
    },
  },
  methods: {
    el(id) {
      return typeof document !== "undefined" ? document.getElementById(id) : null;
    },
    lerp(a, b, e) {
      return a + (b - a) * e;
    },
    ease(p) {
      return p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
    },
    perfectAnim(duration, step) {
      return new Promise((resolve) => {
        const start = performance.now();
        const frame = (now) => {
          const p = Math.min(1, (now - start) / duration);
          step(p);
          if (p < 1) requestAnimationFrame(frame);
          else resolve();
        };
        requestAnimationFrame(frame);
      });
    },
    perfectGoo(B, M, O) {
      const pre = this.el(this.gpreId);
      const mat = this.el(this.gmatId);
      if (pre) pre.setAttribute("stdDeviation", B.toFixed(2));
      if (mat) {
        mat.setAttribute(
          "values",
          `1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 ${M.toFixed(2)} ${O.toFixed(2)}`
        );
      }
    },
    perfectMask(a, b) {
      const shell = this.$refs.shell;
      if (!shell) return;
      shell.style.setProperty("--mA", `${a.toFixed(1)}%`);
      shell.style.setProperty("--mB", `${b.toFixed(1)}%`);
    },
    perfectApply(s) {
      this.perfectGoo(s.B, s.M, s.O);
      this.perfectMask(s.mA, s.mB);
      if (this.$refs.gloss) this.$refs.gloss.style.opacity = String(s.gloss);
    },
    resetPerfectLoader() {
      this.dissolving = false;
      this.orbFilter = "";
      const orb = this.$refs.orb;
      if (orb) {
        orb.style.opacity = "";
        orb.style.transform = "";
      }
      const disp = this.el(this.dispId);
      const blur = this.el(this.blurId);
      if (disp) disp.setAttribute("scale", "0");
      if (blur) blur.setAttribute("stdDeviation", "0");
      this.perfectApply(SPHERE.sphere);
    },
    async morphPerfectLoader() {
      const a = SPHERE.sphere;
      const b = SPHERE.wash;
      await this.perfectAnim(1450, (p) => {
        const e = this.ease(p);
        this.perfectGoo(
          this.lerp(a.B, b.B, e),
          this.lerp(a.M, b.M, e),
          this.lerp(a.O, b.O, e)
        );
        this.perfectMask(this.lerp(a.mA, b.mA, e), this.lerp(a.mB, b.mB, e));
        if (this.$refs.gloss) {
          this.$refs.gloss.style.opacity = String(
            this.lerp(a.gloss, b.gloss, e)
          );
        }
      });
    },
    async dissolvePerfectLoader() {
      const orb = this.$refs.orb;
      this.perfectMask(0, 140);
      this.orbFilter = `url(#${this.wcId})`;
      if (orb) void orb.offsetWidth;
      this.dissolving = true;
      await this.perfectAnim(1500, (p) => {
        const e = 1 - Math.pow(1 - p, 3);
        const disp = this.el(this.dispId);
        const blur = this.el(this.blurId);
        if (disp) disp.setAttribute("scale", (26 * e).toFixed(2));
        if (blur) blur.setAttribute("stdDeviation", (2.4 * e).toFixed(2));
      });
    },
    /** Parent calls this when card 1 is ready to mount — starts dissolve exit. */
    releaseBurst() {
      if (this.resolveGate) {
        this.resolveGate();
        this.resolveGate = null;
      }
    },
    async runSequence() {
      const t0 = performance.now();
      const headlineEl = this.$refs.headline;
      for (const [headline, detail] of this.messages) {
        if (headlineEl) headlineEl.classList.add("change");
        await wait(120);
        this.headline = headline;
        this.detail = detail;
        if (headlineEl) headlineEl.classList.remove("change");
        await wait(1380);
      }
      this.messagesDone = true;
      this.detail = this.morphMessage;
      await this.morphPerfectLoader();
      this.detail = this.openMessage;
      // Se mantiene el estado "wash" hasta que el padre avisa que ya hay
      // contenido (no queda hueco en blanco). Con holdMs > 0 hay una rejilla de
      // seguridad; con holdMs = 0 se espera al padre y nada mas.
      if (this.holdMs > 0) {
        await Promise.race([this.gatePromise, wait(this.holdMs)]);
      } else {
        await this.gatePromise;
      }
      await this.dissolvePerfectLoader();
      this.bursting = true;
      await wait(280);
      console.log(
        "[stream-timing] T_splash_ms=",
        Math.round(performance.now() - t0)
      );
      if (this.resolveDone) this.resolveDone();
    },
    whenDone() {
      return this.donePromise || Promise.resolve();
    },
    whenMessagesDone() {
      return new Promise((resolve) => {
        const tick = () => {
          if (this.messagesDone) resolve();
          else requestAnimationFrame(tick);
        };
        tick();
      });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;1,9..144,400&display=swap");

.loader {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background:
    radial-gradient(circle at 50% 43%, rgba(168, 230, 207, 0.15), transparent 25%),
    radial-gradient(circle at 50% 54%, rgba(145, 74, 207, 0.08), transparent 34%),
    rgba(255, 255, 255, 0.985);
  backdrop-filter: blur(8px);
  border-radius: 28px;
}
.perfect-stage {
  position: relative;
  width: 230px;
  height: 210px;
  display: grid;
  place-items: center;
}
.perfect-stage::before {
  content: "";
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(168, 230, 207, 0.25) 0%,
    rgba(78, 205, 196, 0.08) 43%,
    rgba(145, 74, 207, 0.05) 58%,
    transparent 72%
  );
  filter: blur(8px);
}
.perfect-orb {
  --orb: 170px;
  position: relative;
  width: var(--orb);
  height: var(--orb);
  transform-origin: 50% 50%;
  will-change: transform, opacity, filter;
}
.perfect-shell {
  position: absolute;
  inset: 0;
  -webkit-mask: radial-gradient(
    circle at 50% 50%,
    #000 var(--mA, 59%),
    transparent var(--mB, 61%)
  );
  mask: radial-gradient(
    circle at 50% 50%,
    #000 var(--mA, 59%),
    transparent var(--mB, 61%)
  );
}
.perfect-pigment {
  position: absolute;
  inset: 0;
  animation: perfectSpin 20s linear infinite;
}
.perfect-fluid {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  animation: perfectBlob 6s ease-in-out infinite;
}
.perfect-teal {
  transform-origin: 30% 40%;
  background: radial-gradient(
    ellipse at 24% 30%,
    rgba(120, 226, 216, 0.92) 0%,
    rgba(78, 205, 196, 0.62) 34%,
    transparent 74%
  );
}
.perfect-pink {
  transform-origin: 70% 60%;
  animation-delay: -2s;
  background: radial-gradient(
    ellipse at 70% 60%,
    rgba(255, 150, 190, 0.9) 0%,
    rgba(255, 107, 157, 0.58) 36%,
    transparent 78%
  );
}
.perfect-amber {
  transform-origin: 50% 80%;
  animation-delay: -4s;
  background: radial-gradient(
    ellipse at 50% 78%,
    rgba(255, 222, 120, 0.9) 0%,
    rgba(255, 199, 60, 0.58) 38%,
    transparent 80%
  );
}
.perfect-mint {
  transform-origin: 80% 20%;
  animation-delay: -1s;
  background: radial-gradient(
    ellipse at 80% 24%,
    rgba(214, 193, 242, 0.84) 0%,
    rgba(168, 230, 207, 0.48) 42%,
    rgba(145, 74, 207, 0.12) 66%,
    transparent 83%
  );
}
.perfect-gloss {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  pointer-events: none;
  opacity: 1;
  background:
    radial-gradient(
      circle at 34% 27%,
      rgba(255, 255, 255, 0.72) 0%,
      rgba(255, 255, 255, 0) 32%
    ),
    radial-gradient(
      circle at 71% 23%,
      rgba(185, 133, 229, 0.16) 0%,
      transparent 34%
    ),
    radial-gradient(
      circle at 66% 76%,
      rgba(60, 84, 84, 0.13) 0%,
      transparent 52%
    );
  box-shadow:
    inset 0 -8px 20px rgba(60, 84, 84, 0.13),
    inset 0 6px 16px rgba(255, 255, 255, 0.3);
}
.perfect-orb.dissolve {
  animation: perfectDissolve 1.55s cubic-bezier(0.35, 0, 0.25, 1) forwards;
}
.loader-copy.perfect-copy {
  width: min(540px, 90%);
  margin-top: 4px;
  text-align: center;
}
.loader-eyebrow {
  margin-bottom: 10px;
  color: #6f2da8;
  font-size: 0.63rem;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
}
.perfect-copy h2 {
  min-height: 48px;
  margin: 0;
  color: #334246;
  font-family: "Fraunces", Georgia, serif;
  font-size: clamp(1.55rem, 3.3vw, 2.2rem);
  font-optical-sizing: auto;
  font-weight: 400;
  letter-spacing: -0.015em;
  line-height: 1.08;
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.perfect-copy h2 >>> em,
.perfect-copy h2 em {
  color: #914acf;
  font-style: italic;
}
.perfect-copy h2.change {
  opacity: 0;
  transform: translateY(5px);
}
.perfect-copy span {
  display: block;
  min-height: 22px;
  margin-top: 10px;
  color: #8a9997;
  font-size: 0.82rem;
  font-weight: 500;
  letter-spacing: 0.005em;
}
.dots {
  display: flex;
  justify-content: center;
  gap: 7px;
  margin-top: 13px;
}
.dots i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  animation: dot 1.4s ease-in-out infinite;
}
.dots i:nth-child(1) {
  background: #4ecdc4;
}
.dots i:nth-child(2) {
  background: #914acf;
  animation-delay: 0.18s;
}
.dots i:nth-child(3) {
  background: #ffc107;
  animation-delay: 0.36s;
}
.loader.burst {
  animation: loaderExit 0.42s ease forwards;
}
.loader.burst .loader-copy {
  animation: copyExit 0.34s ease forwards;
}
@keyframes perfectSpin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes perfectBlob {
  0% {
    transform: scale(1) rotate(0) skew(0, 0);
    border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
  }
  25% {
    transform: scale(1.15) rotate(90deg) skew(5deg, -2deg);
    border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%;
  }
  50% {
    transform: scale(0.85) rotate(180deg) skew(-3deg, 4deg);
    border-radius: 70% 30% 40% 60% / 40% 70% 60% 30%;
  }
  75% {
    transform: scale(1.1) rotate(270deg) skew(2deg, -5deg);
    border-radius: 40% 70% 60% 30% / 70% 40% 30% 60%;
  }
  100% {
    transform: scale(1) rotate(360deg) skew(0, 0);
    border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
  }
}
@keyframes perfectDissolve {
  to {
    transform: scale(1.3);
    opacity: 0;
  }
}
@keyframes copyExit {
  to {
    opacity: 0;
    transform: translateY(9px) scale(0.98);
    filter: blur(3px);
  }
}
@keyframes loaderExit {
  to {
    opacity: 0;
    visibility: hidden;
  }
}
@keyframes dot {
  50% {
    transform: translateY(-4px);
    opacity: 0.45;
  }
}
@media (max-width: 650px) {
  .perfect-stage {
    width: 190px;
    height: 172px;
  }
  .perfect-stage::before {
    width: 168px;
    height: 168px;
  }
  .perfect-orb {
    --orb: 145px;
  }
}
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-delay: 0ms !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
